<template>
  <div class="h-full">
    <ShowListingLicenseModal 
      v-if="licenseData"
      :show="showListingLicenseModal" 
      :licenseData="licenseData" 
      @close="showListingLicenseModal = false" 
    />
    <BaseCardTable
      :items="cardData"
      :quickFilterText="quickFilterText"
      :isLoading="isLoading"
      class="md:hidden"
    />

    <PurchasesTable
      title="My Purchases"
      :columns="purchasesColumns"
      :data="paginatedData"
      :totalItems="totalFilteredItems"
      :itemsPerPage="itemsPerPage"
      :redirectableColumns="['listing_name']"
      @redirect="handleRedirect"
      :sortBy="sortBy"
      :sortOrder="sortOrder"
      :showSearch="true"
      :showPagination="true"
      :currentPage="currentPage"
      :showItemsPerPage="true"
      :searchQuery="searchQuery"
      @update:searchQuery="handleSearchQueryUpdate"
      @update:currentPage="handlePageUpdate"
      @update:itemsPerPage="handleItemsPerPageUpdate"
      @sort="handleSort"
      emptyMessage="No purchases found"
      class="hidden md:block"
    >
      
      <template #column-dataset_name="{ item, value }">
        <div class="flex flex-col">
          <span
            class="cursor-pointer font-semibold hover:text-echoes-primary transition-colors duration-200 w-fit truncate max-w-[200px]"
            :title="item.listing_name"
            @click="handleRedirect(item, 'listing_name')"
          >
            {{ item.listing_name }}
          </span>
          <span class="text-white/60 text-sm truncate max-w-[180px]" :title="value">{{ value }}</span>
        </div>
      </template>

      
      <template #column-status="{ value }">
        <span 
          :class="[
            'px-2.5 py-1 text-xs font-medium rounded-full',
            {
              'bg-yellow-500/20 text-yellow-400': value === 'Pending',
              'bg-blue-500/20 text-blue-400': value === 'Ready to Pay',
              'bg-green-500/20 text-green-400': value === 'Payment Completed',
              'bg-red-500/20 text-red-400': value === 'Payment Failed'
            }
          ]"
        >
          {{ value }}
        </span>
      </template>

      
      <template #column-payment="{ item, value }">
        <div class="flex items-center text-white gap-1 hover:text-echoes-primary w-fit transition-colors duration-200 cursor-pointer" @click="copyToClipboard(value)" :title="`Copy to clipboard`">
          <span class="truncate max-w-[120px]" >{{ value }}</span>
          <button
            
          >
            <i class="mdi mdi-content-copy"></i>
          </button>
        </div>
      </template>

      
      <template #column-action="{ item }">
        <div class="flex gap-2">
          <button class="view-license-button" @click="handleViewLicense(item)">
              <i class="uil uil-file-alt text-lg"></i>
              <span class="tooltip">View License</span>
          </button>
          <button 
            :disabled="getButtonConfig(item).isDisabled"
            :class="[
              'flex items-center justify-center gap-2 px-2.5 py-1 text-white rounded-md transition-colors duration-200',
              getButtonConfig(item).isDisabled
                ? 'bg-echoes-background-gray opacity-80 cursor-not-allowed text-white/30'
                : 'bg-echoes-primary hover:bg-echoes-primary/80'
            ]"
            @click="handleButtonClick(item)"
          >
            
            <template v-if="getButtonConfig(item).type === 'download'">
              <svg v-if="getButtonConfig(item).isLoading" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <i v-else class="mdi mdi-arrow-collapse-down"></i>
            </template>
  
            
            <template v-if="getButtonConfig(item).type === 'credentials'">
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                <polyline points="10,17 15,12 10,7"></polyline>
                <line x1="15" y1="12" x2="3" y2="12"></line>
              </svg>
            </template>
  
            
            <template v-if="getButtonConfig(item).type === 'payment'">
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </template>
  
            {{ getButtonConfig(item).label }}
          </button>
        </div>
      </template>

    </PurchasesTable>
    <!-- Credentials Modal -->
    <CredentialsModal 
      :credentialsModalOpen="credentialsModalOpen"
      :credentialsModalSale="credentialsModalSale"
      @closeCredentialsModal="closeCredentialsModal"
      @refreshData="setDatas"
    />
    
    <!-- Checkout Modal -->
    <CheckoutModal 
      v-model:showCheckoutModal="showCheckoutModal" 
      :buyActive="buyActive" 
      :serviceFee="serviceFee" 
      @payment-success="handlePayment" 
      v-if="showCheckoutModal" 
      isFinishSale 
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "@/plugins/axios";
import { useToast } from "@/composables/use-toast";
import { useRouter } from "vue-router";
import PurchasesTable from "@/components/table/Table.vue";
import BaseCardTable from "@/components/table/BaseCardTable.vue";
import CheckoutModal from "@/components/modals/CheckoutModal.vue";
import CredentialsModal from "@/components/modals/CredentialsModal.vue";
import ShowListingLicenseModal from "@/pages/listing/showListingLicenseModal.vue";
const { addToast, removeToast, updateToast } = useToast();
const purchases = ref([]);
const rowData = ref([]);
const cardData = ref([]);
const downloadingId = ref(null);
const router = useRouter();

const isLoading = ref(true);
const credentialsModalOpen = ref(false);
const credentialsModalSale = ref(null);
const showCheckoutModal = ref(false);
const buyActive = ref(null);
const serviceFee = ref(0);
const quickFilterText = ref("");
const sortBy = ref("created_at");
const sortOrder = ref("desc");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchQuery = ref("");

const filteredData = computed(() => {
  if (!searchQuery.value) {
    return rowData.value;
  }
  
  const query = searchQuery.value.toLowerCase();
  return rowData.value.filter(item => 
    item.listing_name?.toLowerCase().includes(query) ||
    item.dataset_name?.toLowerCase().includes(query) ||
    item.seller?.toLowerCase().includes(query) ||
    item.status?.toLowerCase().includes(query) ||
    item.payment?.toLowerCase().includes(query) ||
    item.amount?.toLowerCase().includes(query)
  );
});

const paginatedData = computed(() => filteredData.value);

const totalFilteredItems = computed(() => filteredData.value.length);

onMounted(async () => {
  try {
    isLoading.value = true;
    await setDatas();
  } catch (error) {
    console.error('Error fetching purchases:', error);
    addToast('Error fetching purchases', 'error');
  } finally {
    isLoading.value = false;
  }
});

const purchasesColumns = [
  {
    key: "dataset_name",
    label: "Dataset",
    type: "text",
  },
  {
    key: "seller",
    label: "Seller",
    type: "text",
  },
  {
    key: "payment",
    label: "Payment",
    type: "text",
    sortable: true,
  },
  {
    key: "amount",
    label: "Amount",
    type: "amount",
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    type: "tag",
  },
  {
    key: "created_at",
    label: "Created At",
    type: "date",
    sortable: true,
  },
  {
    key: "action",
    label: "Action",
    type: "button",
  },
];

const handleRedirect = (item, columnKey) => {
  if (columnKey === "listing_name") {
    router.push(`/item-detail/${item.listing_id}`);
  }
};

const handleSort = (columnKey) => {
  if (sortBy.value === columnKey) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = columnKey;
    sortOrder.value = 'asc';
  }
  
  
  rowData.value.sort((a, b) => {
    let aVal = a[columnKey];
    let bVal = b[columnKey];
    
    const nullComparison = (aVal == null) - (bVal == null);
    if (nullComparison !== 0) return nullComparison;
        
    
    const valueTransformers = {
      amount: (val) => parseFloat(val) || 0,
      created_at: (val) => new Date(val).getTime(),
      default: (val) => typeof val === 'string' ? val?.toLowerCase() : val
    };
    
    const transformer = valueTransformers[columnKey] || valueTransformers.default;
    aVal = transformer(aVal);
    bVal = transformer(bVal);
    
    
    let comparison;
    if (aVal > bVal) {
      comparison = 1;
    } else if (aVal < bVal) {
      comparison = -1;
    } else {
      comparison = 0;
    }
    return sortOrder.value === 'asc' ? comparison : -comparison;
  });
  
  
  currentPage.value = 1;
};

const handleSearchQueryUpdate = (query) => {
  searchQuery.value = query;
  currentPage.value = 1; 
};

const handlePageUpdate = (page) => {
  currentPage.value = page;
};

const handleItemsPerPageUpdate = (itemsPerPageValue) => {
  itemsPerPage.value = itemsPerPageValue;
  currentPage.value = 1;
};

const handleButtonClick = async (item) => {
  const config = getButtonConfig(item);
  
  if (config.type === 'download') {
    if (downloadingId.value !== null) {
      addToast('A download is already in progress. Please wait.', 'error');
      return;
    }
    if (item.remaining_downloads <= 0) {
      addToast('You are only able to download files once after making a purchase.', 'warning');
      return;
    }
    downloadFile(item.id);
  } else if (config.type === 'credentials') {
    if (!config.isDisabled) {
      openCredentialsModal(item.credentials_id);
    }
  } else if (config.type === 'payment') {
    showCheckoutModal.value = true;
    buyActive.value = item;
    buyActive.value.seller_account_id = item.seller_account_id;
    serviceFee.value = 0;
  }
};

const formatHistoryStatusLabel = (status) => {
  if (!status) return "Unknown";
  return status === "paid"
    ? "Credentials"
    : status.charAt(0).toUpperCase() + status.slice(1);
};

const getDownloadButtonConfig = (item, extraDisabled = false) => {
  const isLoading = downloadingId.value === item.id;
  return {
    type: "download",
    isDisabled: extraDisabled || downloadingId.value !== null,
    isLoading,
    label: isLoading ? "Downloading..." : "Download",
  };
};

const getExternalSourceButton = (item) => {
  const lastHistoryStatus = item.last_history?.status || item.status;
  let isDisabled;
  let label = formatHistoryStatusLabel(lastHistoryStatus);

  if (item.bid) {
    isDisabled = lastHistoryStatus !== "paid" || item.credentials_used;
    label = "Credentials";
  } else {
    isDisabled =
      (lastHistoryStatus !== "ready" && lastHistoryStatus !== "paid") ||
      item.credentials_used;
  }

  if (lastHistoryStatus === "ready" && !item.bid) {
    return {
      type: "payment",
      isDisabled: false,
      label: "Issue Payment",
    };
  }

  return {
    type: "credentials",
    isDisabled,
    label,
  };
};

const getEvalDownloadButton = (item) => {
  const remainingDefined =
    item.remaining_downloads !== undefined && item.remaining_downloads !== null;
  const noDownloadsRemaining = remainingDefined
    ? item.remaining_downloads <= 0
    : false;
  return getDownloadButtonConfig(item, noDownloadsRemaining);
};

const getRegularDownloadButton = (item) => {
  const noDownloadsRemaining = (item.remaining_downloads || 0) <= 0;
  return getDownloadButtonConfig(item, noDownloadsRemaining);
};

const getButtonConfig = (item) => {
  if (!item.has_downloads && item.external_source) {
    return getExternalSourceButton(item);
  }

  if (item.eval_id) {
    return getEvalDownloadButton(item);
  }

  return getRegularDownloadButton(item);
};

const getStatus = (purchase) => {
  const badges = {
    pending: {
      text: 'Pending',
      color: 'warning',
    },
    ready: {
      text: 'Ready to Pay',
      color: 'info',
    },
    paid: {
      text: 'Payment Completed',
      color: 'success',
    },
    error: {
      text: 'Payment Failed',
      color: 'error',
    },
  };

  let badgeResult = badges.pending;

  const lastHistory = getLastHistory(purchase);
  const statusNotPending = ['ready', 'paid'];

  if(lastHistory && statusNotPending.includes(lastHistory.status)) {
    badgeResult = badges[lastHistory.status];
  } else if(purchase.payments?.length > 0 || purchase.paid) {
    badgeResult = badges.paid;
  }
  return badgeResult;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    addToast("Copied to clipboard!", "success");
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    addToast("Failed to copy to clipboard", "error");
  }
};

const getLastHistory = (sale) => {
  const lastHistory = sale.history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  if(!lastHistory) return null;
  return {
    status: lastHistory?.status?.name || 'N/A',
    step: lastHistory?.step?.name || 'N/A',
    created_at: lastHistory?.created_at || 'N/A',
    message: lastHistory?.message || 'N/A',
  };
};

const openCredentialsModal = (credentials_id) => {
  credentialsModalOpen.value = true;
  credentialsModalSale.value = purchases.value.find(sale => sale.credentials.find(cred => cred.id === credentials_id));
};

const closeCredentialsModal = () => {
  credentialsModalOpen.value = false;
  credentialsModalSale.value = null;
};

const handlePayment = async () => {
  showCheckoutModal.value = false;
  serviceFee.value = 0;
  openCredentialsModal(buyActive.value.credentials_id);
  buyActive.value = null;
  await setDatas();
};

const verifyToShow = async(purchases) => {
  return purchases.filter(purchase => {
    const lastHistory = getLastHistory(purchase);
    const isBid = !!purchase.bid;
    return isBid ? lastHistory?.status === 'paid' : true;
  });
};

const setDatas = async () => {
  const response = await axios.get('/api/sales');
  if (response.status === 200) {
    purchases.value = await verifyToShow(response.data);
    rowData.value = purchases.value.map(purchase => {
      const status = getStatus(purchase);
      return {
        id: purchase?.id,
        listing_id: purchase?.listing?.id,
        dataset_name: purchase?.listing?.datasets?.[0]?.name,
        listing_name: purchase?.listing?.name,
        seller: purchase?.listing?.datasets?.[0]?.seller?.user?.username,
        seller_id: purchase?.listing?.datasets?.[0]?.seller?.user?.id,
        receipt_address: purchase?.listing?.receipt_address,
        seller_account_id: purchase?.listing?.datasets?.[0]?.seller?.stripe_account_id,
        amount: purchase.final_price,
        payment: purchase.tx_hash ? purchase.tx_hash : 'N/A',
        status: status.text,
        remaining_downloads: purchase.downloads?.[0]?.remaining_downloads,
        has_downloads: purchase.downloads && purchase.downloads.length > 0,
        credentials_id: purchase.credentials?.[0]?.id,
        credentials_used: purchase.credentials?.[0]?.used,
        last_history: getLastHistory(purchase),
        bid: purchase.bid,
        created_at: purchase.created_at,
        license_id: purchase.license_id,
        years_duration: purchase.license_years_duration,
        custom_clauses: purchase.license_custom_clauses,
        eval_id: purchase.listing?.datasets?.[0]?.eval_id,
        dataset_id: purchase.listing?.datasets?.[0]?.id,
        file_size: purchase.listing?.datasets?.[0]?.file_size,
        file_name: purchase.listing?.datasets?.[0]?.file_name,
        external_source: purchase.listing?.datasets?.[0]?.external_source,
      }
    });
    cardData.value = purchases.value.map(purchase => {
      const status = getStatus(purchase);
      return {
        id: purchase.id,
        title: purchase.listing?.name,
        actionEnabled: purchase.downloads && purchase.downloads.length > 0 ? purchase.downloads[0].remaining_downloads > 0 : true,
        rows: [
          {
            column: 'Dataset',
            type: 'text',
            value: purchase.listing?.datasets?.[0]?.name,
          },
          {
            column: 'Seller',
            type: 'text',
            value: purchase.listing?.datasets?.[0]?.seller?.user?.username,
          },
          {
            column: 'Amount',
            type: 'text',
            value: purchase.final_price,
          },
          {
            column: 'Status',
            type: 'tag',
            value: status.text,
            color: status.color,
          },
          {
            column: 'Date',
            type: 'date',
            value: purchase.created_at,
          }
        ],
      }
    });
  } else {
    addToast('Error fetching purchases', 'error');
  }
};
const validatePurchase = (sale_id) => {
  if (purchases.value.length === 0) {
    throw new Error("No purchases found");
  }

  if (!sale_id) {
    throw new Error("Invalid purchase ID");
  }

  const purchaseIndex = purchases.value.findIndex((p) => p.id === sale_id);
  if (purchaseIndex === -1) {
    throw new Error("Purchase not found");
  }

  const purchase = purchases.value[purchaseIndex];
  if (
    !purchase.downloads?.[0] ||
    purchase.downloads[0].remaining_downloads <= 0
  ) {
    throw new Error("You have no remaining downloads for this dataset");
  }

  return purchase;
};

const setupDownloadRequest = (sale_id) => {
  const baseURL = axios.defaults.baseURL || "http://localhost:3000";
  const token = localStorage.getItem("authToken");
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return {
    url: `${baseURL}/api/downloads/${sale_id}`,
    headers,
  };
};

const trackDownloadProgress = async (reader, contentLength, loadingToastId) => {
  const chunks = [];
  let receivedLength = 0;
  let lastProgressUpdate = 0;
  let done = false;

  while (!done) {
    const result = await reader.read();
    done = result.done;

    if (!done) {
      chunks.push(result.value);
      receivedLength += result.value.length;

      if (contentLength) {
        const percentComplete = Math.round(
          (receivedLength / parseInt(contentLength)) * 100
        );
        if (percentComplete >= lastProgressUpdate + 5) {
          updateToast(loadingToastId, `Downloading: ${percentComplete}%`);
          lastProgressUpdate = percentComplete;
        }
      }
    }
  }

  return { chunks, receivedLength };
};

const saveDownloadedFile = (chunks, receivedLength, filename) => {
  const allChunks = new Uint8Array(receivedLength);
  let position = 0;

  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }

  const blob = new Blob([allChunks], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

const updatePurchaseState = (sale_id, purchase) => {
  if (purchase.downloads?.[0]) {
    purchase.downloads[0].remaining_downloads = 0;
    const rowIndex = rowData.value.findIndex((row) => row.id === sale_id);
    if (rowIndex !== -1) {
      rowData.value[rowIndex].remaining_downloads = 0;
    }
  }
};

const downloadFile = async (sale_id, dataset_id) => {
  let loadingToastId;
  try {
    if (dataset_id) {
      await downloadTaskBundles(dataset_id);
    } else {
      downloadingId.value = sale_id;

      const purchase = validatePurchase(sale_id);

      loadingToastId = addToast("Preparing download...", "loading");
      const { url, headers } = setupDownloadRequest(sale_id);

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => "Unknown error");
        throw new Error(error.message);
      }

      const { chunks, receivedLength } = await trackDownloadProgress(
        response.body.getReader(),
        response.headers.get("content-length"),
        loadingToastId
      );

      const contentDisposition = response.headers.get("content-disposition");
      const datasetFilename = contentDisposition.split("\"")[1];
      console.log('datasetFilename => ', datasetFilename);

      saveDownloadedFile(chunks, receivedLength, datasetFilename);
      updatePurchaseState(sale_id, purchase);

      removeToast(loadingToastId);
      addToast("Download complete!", "success");
    }
  } catch (error) {
    console.error("Error downloading file:", error);
    removeToast(loadingToastId);
    addToast(`Download failed: ${error.message}`, "error");
  } finally {
    downloadingId.value = null;
  }
};

const showListingLicenseModal = ref(false);
const licenseData = ref(null);

const handleViewLicense = (item) => {
  showListingLicenseModal.value = true;
  licenseData.value = {
    licenseId: item.license_id,
    yearsDuration: item.years_duration,
    customClauses: item.custom_clauses,
  };
};

const downloadTaskBundles = async (dataset_id) => {
    if (!dataset_id) {
        addToast('Dataset ID not found', 'error');
        return;
    }

    let loadingToastId;
    try {
        const datasetId = dataset_id;
        
        loadingToastId = addToast('Preparing download...', 'loading');

        const baseURL = axios.defaults.baseURL || 'http://localhost:3000';
        const token = localStorage.getItem('authToken');
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const url = `${baseURL}/api/eval-runs/${datasetId}/download-task-bundles-file`;

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || 'Download failed');
        }

        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;
        let done = false;

        while (!done) {
            const result = await reader.read();
            done = result.done;

            if (!done) {
                chunks.push(result.value);
                receivedLength += result.value.length;
            }
        }

        const allChunks = new Uint8Array(receivedLength);
        let position = 0;

        for (const chunk of chunks) {
            allChunks.set(chunk, position);
            position += chunk.length;
        }

        const filename = `task-bundles-${datasetId}.tar.gz`;
        const blob = new Blob([allChunks], { type: 'application/gzip' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(link);

        removeToast(loadingToastId);
        addToast('Download complete!', 'success');
    } catch (error) {
        console.error('Error downloading task bundles:', error);
        if (loadingToastId) {
            removeToast(loadingToastId);
        }
        addToast(`Download failed: ${error.message}`, 'error');
    } finally {
      downloadingId.value = null;
    }
};
</script>

<style scoped lang="postcss">
.creator-profile__hero {
  @apply relative pt-16 mb-3 border-b border-gray-200 dark:border-white/10 bg-gray-100;
  background-size: 100% 100%;
  background-position: 0px 0px;
  background-repeat: no-repeat;
}

.dark .creator-profile__hero {
  background: linear-gradient(to bottom, #1e293b, #0f172a, #020617);
}

.creator-profile__container {
  @apply relative overflow-hidden w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20;
}

/* Add smooth transition for tooltip */
.transition-transform {
  transition: transform 0.15s ease-in-out;
}

.tooltip {
  @apply absolute left-1/2 -translate-x-1/2 bottom-full px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-black rounded-lg whitespace-normal w-max max-w-[300px];
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s ease-in-out;
  z-index: 50;
}

.tooltip::after {
  content: '';
  @apply absolute left-1/2 -translate-x-1/2 -bottom-1 border-4 border-transparent border-t-gray-900;
}

.view-license-button:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.view-license-button {
  @apply text-white/80 hover:text-echoes-primary transition-colors relative bg-echoes-background-gray rounded-md p-0.5 px-1;
}


</style>